import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { X, MessageCircle, Send, Loader2, Star } from 'lucide-react';

import { BACKEND_URL } from '../utils/api';
const API = `${BACKEND_URL}/api`;

const CHAT_STRINGS = {
  en: {
    title: 'AI Assistant',
    statusOnline: 'Online • Ready to help',
    greeting: "👋 Hi there! I'm your AI assistant. I'd love to help you with your website needs. May I have your name?",
    leadSaved: "✅ Perfect! I've saved your information. Someone from our team will reach out to you shortly. Is there anything else I can help you with?",
    errorReply: "I'm sorry, I encountered an issue. Please try again or contact us directly.",
    inputPlaceholder: 'Type your message...',
    closeAria: 'Close chat',
    reviewCta: 'Love our service? Leave us a Google Review!',
    poweredBy: 'Powered by Web-TechServices-AI',
  },
  es: {
    title: 'Asistente IA',
    statusOnline: 'En línea • Listo para ayudar',
    greeting: "👋 ¡Hola! Soy tu asistente de IA. Me encantaría ayudarte con las necesidades de tu sitio web. ¿Podrías decirme tu nombre?",
    leadSaved: "✅ ¡Perfecto! Guardé tu información. Alguien de nuestro equipo se pondrá en contacto contigo pronto. ¿Puedo ayudarte con algo más?",
    errorReply: "Lo siento, tuve un problema. Por favor inténtalo de nuevo o contáctanos directamente.",
    inputPlaceholder: 'Escribe tu mensaje...',
    closeAria: 'Cerrar chat',
    reviewCta: '¿Te encanta nuestro servicio? ¡Déjanos una reseña en Google!',
    poweredBy: 'Desarrollado por Web-TechServices-AI',
  },
};

function AIChatWidget() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0] === 'es' ? 'es' : 'en';
  const s = CHAT_STRINGS[lang];
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [hasGreeted, setHasGreeted] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Generate unique session ID
    setSessionId(`chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  }, []);

  useEffect(() => {
    // Auto-greet when chat opens for the first time
    if (isOpen && !hasGreeted && messages.length === 0) {
      setHasGreeted(true);
      setTimeout(() => {
        addBotMessage(s.greeting);
      }, 500);
    }
  }, [isOpen, hasGreeted, messages.length, s.greeting]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const addBotMessage = (text) => {
    setMessages(prev => [...prev, {
      type: 'bot',
      text,
      timestamp: new Date().toISOString()
    }]);
  };

  const addUserMessage = (text) => {
    setMessages(prev => [...prev, {
      type: 'user',
      text,
      timestamp: new Date().toISOString()
    }]);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    addUserMessage(userMessage);
    setInputValue('');
    setIsTyping(true);

    try {
      // Send to AI chat endpoint
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId,
        conversation_history: messages,
        lang,
      });

      setIsTyping(false);
      addBotMessage(response.data.response);

      // Check if lead is complete and save
      if (response.data.lead_complete) {
        // Lead captured successfully
        setTimeout(() => {
          addBotMessage(s.leadSaved);
        }, 1000);
      }

    } catch (error) {
      console.error('Chat error:', error);
      setIsTyping(false);
      addBotMessage(s.errorReply);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-4 w-14 h-14 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform z-50"
        >
          <MessageCircle className="w-7 h-7 text-white" />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed inset-y-4 right-4 sm:right-6 sm:top-auto sm:bottom-6 w-96 max-w-[calc(100vw-2rem)] sm:h-[600px] sm:max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl flex flex-col z-50 border border-slate-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-t-2xl flex justify-between items-center shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <span className="text-2xl">🤖</span>
              </div>
              <div>
                <p className="font-bold">{s.title}</p>
                <p className="text-xs opacity-90">{s.statusOnline}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 p-2 rounded-full transition"
              aria-label={s.closeAria}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.type === 'user'
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-br-none'
                      : 'bg-white text-slate-800 rounded-bl-none shadow-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  <p className={`text-xs mt-1 ${msg.type === 'user' ? 'text-blue-100' : 'text-slate-400'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white p-3 rounded-2xl rounded-bl-none shadow-md">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-200 bg-white rounded-b-2xl">
            {/* Google Review Link */}
            <a
              href="https://g.page/r/CSGC2YNQow5sEAI/review"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mb-3 py-2 px-4 bg-gradient-to-r from-amber-400 to-yellow-500 text-white rounded-full text-sm font-medium hover:shadow-lg transition-all hover:scale-105"
            >
              <Star className="w-4 h-4 fill-current" />
              {s.reviewCta}
            </a>
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={s.inputPlaceholder}
                className="flex-1 px-4 py-3 border border-slate-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isTyping}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-3 rounded-full hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTyping ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2 text-center">
              {s.poweredBy}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default AIChatWidget;
